import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { toast } from "sonner";

interface AccessCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  selectedFeature: string;
  existingCode?: string;
  isUpdate?: boolean;
}

export const AccessCodeDialog = ({ 
  open, 
  onOpenChange, 
  onSuccess, 
  selectedFeature,
  existingCode,
  isUpdate = false 
}: AccessCodeDialogProps) => {
  const [accessCode, setAccessCode] = useState(existingCode || "");
  const [isLoading, setIsLoading] = useState(false);
  const user = useUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Vérifier d'abord la session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast.error("Vous devez être connecté");
      return;
    }

    if (!accessCode.trim()) {
      toast.error("Le code d'accès ne peut pas être vide");
      return;
    }

    console.log("Submitting access code:", {
      isUpdate,
      selectedFeature,
      accessCode,
      userId: session.user.id
    });

    setIsLoading(true);
    try {
      if (isUpdate) {
        const { error } = await supabase
          .from("access_controls")
          .update({
            access_code: accessCode,
            updated_at: new Date().toISOString()
          })
          .eq('owner_id', session.user.id)
          .eq('feature_name', selectedFeature);

        if (error) {
          console.error("Error updating access code:", error);
          throw error;
        }
        toast.success("Code d'accès modifié avec succès");
      } else {
        // Insert new access code
        const { error } = await supabase
          .from("access_controls")
          .insert({
            owner_id: session.user.id,
            feature_name: selectedFeature,
            access_code: accessCode,
            is_disabled: false // Make sure new codes are enabled by default
          });

        if (error) {
          console.error("Error adding access code:", error);
          throw error;
        }
        toast.success("Code d'accès ajouté avec succès");
      }

      onSuccess();
      setAccessCode("");
      onOpenChange(false);
    } catch (error) {
      console.error("Error managing access code:", error);
      toast.error("Erreur lors de la gestion du code d'accès");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isUpdate ? "Modifier le code d'accès" : "Ajouter un code d'accès"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="accessCode">Code d'accès</Label>
            <Input
              id="accessCode"
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Entrez le code d'accès"
              required
            />
          </div>
          <Button 
            type="submit" 
            disabled={isLoading || !accessCode.trim()}
            className="w-full"
          >
            {isLoading ? "En cours..." : isUpdate ? "Modifier" : "Ajouter"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};